import type { Scope } from "./db";
import { type OutboxEntry, subscribe } from "./outbox";

/** Run the work one at a time. A call during a run asks for one more run after it, not a second run at the same time. */
export function oneAtATime(work: () => Promise<void>): () => Promise<void> {
  let running: Promise<void> | null = null;
  let again = false;
  const run = async (): Promise<void> => {
    do {
      again = false;
      await work().catch(() => undefined);
    } while (again);
  };
  return () => {
    if (running) {
      again = true;
      return running;
    }
    running = run().finally(() => {
      running = null;
    });
    return running;
  };
}

/** True when the server day changes after the entry: it was refused, or a visit or a pickup was sent. */
export const changesDay = (entry: OutboxEntry) => entry.state === "failed" || (entry.state === "sent" && (entry.kind === "visit" || entry.kind === "pickup"));

/** Load the day again when the phone goes online, and at the end of a send pass that changed the server day. */
export function watchDay(scope: Scope, load: () => void, events: EventTarget = window): () => void {
  let stale = false;
  const stop = subscribe(scope, (change) => {
    if (change) {
      if (changesDay(change.entry)) stale = true;
      return;
    }
    if (!stale) return;
    stale = false;
    console.info("day reload: a send pass changed the day");
    load();
  });
  const online = () => {
    console.info("day reload: the phone is online");
    load();
  };
  events.addEventListener("online", online);
  return () => {
    stop();
    events.removeEventListener("online", online);
  };
}
